// Role-based navigation guard (doc 04 §8.3).
//
// Order of enforcement:
//   1. Public, guest-readable routes pass through.
//   2. requiresAuth → must be authenticated (else redirect to login w/ ?redirect).
//   3. meta.roles    → user must hold AT LEAST ONE listed role (any-of).
//   4. meta.permissions → user must hold ALL listed permissions (all-of).
// Falls back to /403 on an authorization miss, /login on an auth miss.
import type { NavigationGuardWithThis } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'

export const roleGuard: NavigationGuardWithThis<undefined> = async (to) => {
  const auth = useAuthStore()

  // 1. Public, guest-readable routes (rankings, profiles, live scores, results).
  if (to.meta.public && !to.meta.requiresAuth) {
    return true
  }

  // Hydrate the session once on a hard refresh if we have a token but no user yet.
  if (auth.token && !auth.user) {
    try {
      await auth.fetchMe()
    } catch {
      auth.clearSession()
    }
  }

  // 2. Authentication gate.
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  // 3. Role gate — any-of.
  if (to.meta.roles?.length && !auth.hasAnyRole(to.meta.roles)) {
    return { name: 'forbidden' }
  }

  // 4. Permission gate — all-of.
  if (
    to.meta.permissions?.length &&
    !to.meta.permissions.every((p) => auth.can(p))
  ) {
    return { name: 'forbidden' }
  }

  return true
}
